import type { Tag } from '__types__';
import type { ReduxProxy } from 'v5/api/_common/reduxProxy';
import { ShortlistApi } from 'v5/api/shortlist/shortlist.api';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { SystemEffect } from 'v5/services/internal/MessageService/_private/SystemEffectMessage';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { ShortlistMutationService } from './ShortlistMutationService';

export function createShortlistMutationService(
  config: WithServices<
    [WithMessageService, WithErrorService, WithTagService]
  > & {
    api: ShortlistApi;
    reduxProxy: Pick<ReduxProxy, 'write'>;
  }
): ShortlistMutationService {
  const {
    api,
    reduxProxy,
    services: { messageService, errorService, tagsService }
  } = config;
  const interceptor = errorService.withCategory('ShortlistMutationService');
  // Tags typed in an attempt whose create failed: a retry reuses them, since
  // the server keeps no unique tag names. A created shortlist drops its own,
  // which may be deleted before the next one is typed.
  const typedTags = new Map<string, Promise<Tag>>();
  const typedTagKey = (label: string) => label.trim().toLocaleLowerCase();

  function addTypedTag(label: string) {
    const key = typedTagKey(label);
    const known = typedTags.get(key);
    if (known) return known;
    const created = tagsService.addTag('shortlist', label);
    typedTags.set(key, created);
    created.catch(() => typedTags.delete(key));
    return created;
  }

  async function reportEffectAndWaitUntilCacheReady(
    scope: SystemEffect[] = []
  ) {
    await messageService.reportSystemEffect.sendRequest(
      {
        scope
      },
      3000
    );
  }

  return {
    create: interceptor.wrap(async input => {
      // The legacy modal sent whole tag rows and created typed tags first.
      const tags = await Promise.all(
        input.tags.map(ref =>
          ref.type === 'existing'
            ? tagsService.resolveById('shortlist', ref.id)
            : addTypedTag(ref.label)
        )
      );
      const result = await api.createShortlist({ ...input, tags });
      for (const ref of input.tags) {
        if (ref.type === 'new') typedTags.delete(typedTagKey(ref.label));
      }
      reduxProxy.write.emit({ type: 'CREATE_SHORTLIST_SUCCESS', result });
      // The caller moves on to the new shortlist; the lists refresh behind it.
      void reportEffectAndWaitUntilCacheReady([
        {
          entity: 'shortlist',
          entityId: result.id,
          payload: { type: 'create' }
        }
      ]).catch(() => undefined);
      return result;
    }),

    archiveShortlists: interceptor.wrap(async request => {
      try {
        await api.bulkArchive(request);

        // Report system effects for successfully archived shortlists
        await reportEffectAndWaitUntilCacheReady(
          request.ids.map(id => ({
            entity: 'shortlist',
            entityId: id,
            payload: { type: 'update' }
          }))
        );
      } catch (error) {
        console.error('Failed to archive shortlists:', error);
        throw new Error('Failed to archive shortlists');
      }
    }),

    unarchiveShortlists: interceptor.wrap(async request => {
      try {
        await api.bulkUnarchive(request);

        // Report system effects for successfully unarchived shortlists
        await reportEffectAndWaitUntilCacheReady(
          request.ids.map(id => ({
            entity: 'shortlist',
            entityId: id,
            payload: { type: 'update' }
          }))
        );
      } catch (error) {
        console.error('Failed to unarchive shortlists:', error);
        throw new Error('Failed to unarchive shortlists');
      }
    })
  };
}
